// src/components/SenebiTab.js

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { db, auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';
import { collection, onSnapshot, query, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { formatCurrency, formatNumberForDisplay, parseArgentineNumber } from '../utils/formatters';
import { compareValues } from '../utils/sortHelpers';
import { renderCommentCell } from '../utils/uiHelpers';
import { TrashIcon } from './Icons';
import { theme } from '../config/theme';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { Button, CircularProgress, Box, Typography, Input } from '@mui/material';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import useImportSenebiReminder from '../hooks/useImportSenebiReminder';
import ImportSenebiReminderModal from './ImportSenebiReminderModal';
import MobileCollapsibleSection from './common/MobileCollapsibleSection';
import { useStackedTable } from '../hooks/useStackedTable';

// --- Estilos ---
const TabContainer = styled.div``;
const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;
const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.titleFamily};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.75rem;
  margin: 0;
`;
const SubTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.titleFamily};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  margin: 2rem 0 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 0.5rem;
`;
const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-family: ${({ theme }) => theme.typography.bodyFamily};
  font-size: 0.9rem;
  min-width: 240px;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;
const IndicatorsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;
const Indicator = styled.div`
  text-align: center; flex-grow: 1;
  & > strong {
    font-family: ${({ theme }) => theme.typography.titleFamily};
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.25rem; display: block;
  }
  & > span {
    font-size: 0.85rem; color: ${({ theme }) => theme.colors.textSecondary};
  }
`;
const GridContainer = styled.div`
    height: 500px;
    width: 100%;
    margin-bottom: 1rem;
    overflow-x: auto;
`;
const ButtonsContainer = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
`;
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
`;
const Table = styled.table`
  width: 100%;
  min-width: 1800px;
  border-collapse: collapse;
  th, td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0.875rem;
  }
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;
const StyledTableRow = styled.tr`
  background-color: ${props => props.$isLoaded ? '#e8f5e9' : 'transparent'};
  &:hover {
    background-color: ${props => props.$isLoaded ? '#c8e6c9' : '#f1f3f5'};
  }
`;
const ActionButton = styled.button`
  background-color: transparent; border: none;
  cursor: pointer; padding: 5px;
  color: ${props => props.color || props.theme.colors.textSecondary};
  &:hover { opacity: 0.7; }
`;
const ActionContainer = styled.div`
    display: flex;
    gap: 0.5rem;
`;
const CargadaButton = styled.button`
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    border: 1px solid;
    font-size: 0.75rem;
    font-weight: 600;
    background-color: ${props => props.$isLoaded ? props.theme.colors.success : 'transparent'};
    border-color: ${props => props.$isLoaded ? '#10B981' : '#D1D5DB'};
    color: ${props => props.$isLoaded ? '#065F46' : '#4B5563'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.disabled ? 0.6 : 1};
    &:hover {
        opacity: ${props => props.disabled ? 0.6 : 0.8};
    }
`;

const operationSchema = Yup.object().shape({
    fecha: Yup.string().matches(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, 'Formato de fecha debe ser YYYY-MM-DD').required('La fecha es obligatoria'),
    operacion: Yup.string().oneOf(['Compra', 'Venta']).required('La operación es obligatoria'),
    especie: Yup.string().required('La especie es obligatoria'),
    plazo: Yup.string().oneOf(['24Hs', '48Hs', 'CI', 'T+0', 'T+1', 'T+2', 'T+3']).required('El plazo es obligatorio'),
    cliente: Yup.string().required('El cliente es obligatorio'),
    cantidad: Yup.number().typeError('Debe ser un número').positive('La cantidad debe ser un número positivo').required('La cantidad es obligatoria'),
    precio: Yup.number().typeError('Debe ser un número').positive('El precio debe ser un número positivo').required('El precio es obligatorio'),
    moneda: Yup.string().oneOf(['Pesos', 'USD Mep', 'USD CCL']).required('La moneda es obligatoria'),
    precioCarteraPropia: Yup.number().typeError('Debe ser un número').positive('El precio CP debe ser un número positivo').required('El precio CP es obligatorio'),
    mercado: Yup.string().default('Senebi'),
    nroComitente: Yup.string(),
    arancelPPT: Yup.number().typeError('Debe ser un número').default(0),
    arancelROSVAL: Yup.number().typeError('Debe ser un número').default(0),
    ddm: Yup.number().typeError('Debe ser un número').default(0),
    fee: Yup.number().typeError('Debe ser un número').default(0),
    observaciones: Yup.string().default(''),
    cargada: Yup.string().oneOf(['Si', 'No']).default('No')
});

const SenebiTab = ({ userRole, userName, onViewComment }) => {
  const [operations, setOperations] = useState([]);
  const [feeTotals, setFeeTotals] = useState({ Pesos: 0, "USD Mep": 0, "USD CCL": 0 });
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'descending' });
  const [clienteFilter, setClienteFilter] = useState('');
  const stackedTable = useStackedTable({
    title: 'Cliente',
    primary: ['ID', 'Operación', 'Especie', 'Cantidad', 'Precio', 'Monto', 'Ganancia', 'Cargada'],
    actions: 'Acciones',
  });
  const [gridRows, setGridRows] = useState([
      { 
        id: 1, 
        fecha: '', 
        operacion: 'Compra', 
        especie: '', 
        plazo: '24Hs', 
        cliente: '', 
        cantidad: null, 
        precio: null, 
        moneda: 'Pesos',
        precioCarteraPropia: null, 
        mercado: 'Senebi',
        nroComitente: '', 
        arancelPPT: 0, 
        arancelROSVAL: 0, 
        ddm: 0, 
        fee: 0, 
        monto: 0,
        ganancia: 0,
        observaciones: '',
        cargada: 'No'
      }
  ]);
  const [activeCell, setActiveCell] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedOperations, setSelectedOperations] = useState([]);

    const canModify = userRole === 'Trader' || userRole === 'Admin' || userRole === 'SuperAdmin';

  // Hook para recordatorio de importación SENEBI (solo para Traders)
  const {
    showReminder,
    currentTime,
    dismissReminder,
    handleImportAction,
    forceShowReminder
  } = useImportSenebiReminder();

  // Exponer forceShowReminder para testing (disponible en todos los ambientes)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.forceShowSenebiReminder = forceShowReminder;
      console.log('🔧 Testing function available: window.forceShowSenebiReminder()');
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete window.forceShowSenebiReminder;
      }
    };
  }, [forceShowReminder]);

  // Solo mostrar recordatorio si es Trader
  const shouldShowReminder = showReminder && userRole === 'Trader';

  // Estado inicial

  useEffect(() => {
    const q = query(collection(db, "senebiOperations"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ops = [];
      let totals = { Pesos: 0, "USD Mep": 0, "USD CCL": 0 };
      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        ops.push(data);
        // Usar directamente el campo ganancia que viene del CSV (columna R)
        const ganancia = parseFloat(data.ganancia) || 0;
        const moneda = data.moneda || 'Pesos';
        if (totals.hasOwnProperty(moneda)) {
          totals[moneda] += ganancia;
        }
      });
      const sortedOps = ops.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)); // Sort descending
      const opsWithSequentialId = sortedOps.map((op, index) => ({ ...op, sequentialId: sortedOps.length - index }));
      setOperations(opsWithSequentialId);
      setFeeTotals(totals);
    });
    return () => unsubscribe();
  }, []);

  const validateCell = async (field, value) => {
      try {
          await Yup.reach(operationSchema, field).validate(value);
          return null;
      } catch (error) {
          return error.message;
      }
  };

  const gridColumns = [
    { field: 'fecha', headerName: 'Fecha', width: 120, editable: true, description: 'Formato: YYYY-MM-DD', preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('fecha', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'operacion', headerName: 'Operación', width: 110, editable: true, type: 'singleSelect', valueOptions: ['Compra', 'Venta'] },
    { field: 'especie', headerName: 'Especie', width: 100, editable: true, preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('especie', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'plazo', headerName: 'Plazo', width: 90, editable: true, type: 'singleSelect', valueOptions: ['24Hs', '48Hs', 'CI', 'T+0', 'T+1', 'T+2', 'T+3'] },
    { field: 'cliente', headerName: 'Cliente', width: 150, editable: true, preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('cliente', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'cantidad', headerName: 'Cantidad', width: 120, editable: true, type: 'number', preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('cantidad', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'precio', headerName: 'Precio', width: 100, editable: true, type: 'number', preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('precio', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'monto', headerName: 'Monto', width: 120, editable: false, type: 'number' },
    { field: 'moneda', headerName: 'Moneda', width: 100, editable: true, type: 'singleSelect', valueOptions: ['Pesos', 'Dólares Mep', 'Dólares Cable', 'USD Mep', 'USD CCL'] },
    { field: 'precioCarteraPropia', headerName: 'Precio CP', width: 110, editable: true, type: 'number', preProcessEditCellProps: async (params) => { const errorMessage = await validateCell('precioCarteraPropia', params.props.value); return { ...params.props, error: !!errorMessage, helperText: errorMessage }; } },
    { field: 'mercado', headerName: 'Mercado', width: 100, editable: true, type: 'singleSelect', valueOptions: ['Senebi', 'BYMA', 'MAE'] },
    { field: 'nroComitente', headerName: 'OPERADOR', width: 130, editable: true },
    { field: 'arancelPPT', headerName: 'Arancel PPT', width: 110, editable: true, type: 'number' },
    { field: 'arancelROSVAL', headerName: 'Arancel ROSVAL', width: 130, editable: true, type: 'number' },
    { field: 'ddm', headerName: 'DDM', width: 80, editable: true, type: 'number' },
    { field: 'fee', headerName: 'Fee', width: 100, editable: false, type: 'number' },
    { field: 'ganancia', headerName: 'Ganancia', width: 120, editable: true, type: 'number' },
    { field: 'observaciones', headerName: 'OBSERVACIONES', width: 150, editable: true },
    { 
      field: 'actions', 
      headerName: 'Acciones', 
      width: 80, 
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <ActionButton 
          color={theme.colors.danger} 
          onClick={() => handleDeleteGridRow(params.row.id)} 
          title="Eliminar fila"
        >
          <TrashIcon />
        </ActionButton>
      )
    }
  ];

  const handleAddNewRow = () => {
    const newId = gridRows.length > 0 ? Math.max(...gridRows.map(r => r.id)) + 1 : 1;
    setGridRows(prevRows => [...prevRows, { 
      id: newId, 
      fecha: '', 
      operacion: 'Compra', 
      especie: '', 
      plazo: '24Hs', 
      cliente: '', 
      cantidad: null, 
      precio: null, 
      moneda: 'Pesos',
      precioCarteraPropia: null, 
      mercado: 'Senebi',
      nroComitente: '', 
      arancelPPT: 0, 
      arancelROSVAL: 0, 
      ddm: 0, 
      fee: 0, 
      monto: 0,
      ganancia: 0,
      observaciones: '',
      cargada: 'No'
    }]);
  };

  const handleCellKeyDown = (params, event) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      const lastColumnField = gridColumns[gridColumns.length - 1].field;
      const lastRowId = gridRows[gridRows.length - 1].id;
      if (params.field === lastColumnField && params.id === lastRowId) {
        event.preventDefault();
        handleAddNewRow();
      }
    }
  };

  const handleCellClick = (params) => setActiveCell({ id: params.id, field: params.field });

  const handlePaste = (event) => {
    try {
        if (!activeCell) return;
        event.preventDefault();
        const clipboardData = event.clipboardData.getData('text');
        const lines = clipboardData.trim().split('\n');
        if (lines.length === 0) return;
        const pastedData = lines.map(line => line.split('\t'));
        const startRowIndex = gridRows.findIndex(row => row.id === activeCell.id);
        const startColumnIndex = gridColumns.findIndex(col => col.field === activeCell.field);
        if (startRowIndex === -1 || startColumnIndex === -1) return;
        let updatedRows = [...gridRows];
        let newRowsToAdd = [];
        let maxId = gridRows.length > 0 ? Math.max(...gridRows.map(r => r.id)) : 0;
        pastedData.forEach((pastedRow, rowIndex) => {
            const currentRowIndex = startRowIndex + rowIndex;
            if (currentRowIndex >= updatedRows.length) {
                maxId++;
                let newRow = { 
                  id: maxId, 
                  fecha: '', 
                  operacion: 'Compra', 
                  especie: '', 
                  plazo: '24Hs', 
                  cliente: '', 
                  cantidad: null, 
                  precio: null, 
                  moneda: 'Pesos',
                  precioCarteraPropia: null, 
                  mercado: 'Senebi',
                  nroComitente: '', 
                  arancelPPT: 0, 
                  arancelROSVAL: 0, 
                  ddm: 0, 
                  fee: 0, 
                  monto: 0,
                  ganancia: 0,
                  observaciones: '',
                  cargada: 'No'
                };
                pastedRow.forEach((cellValue, cellIndex) => {
                    const currentColumnIndex = startColumnIndex + cellIndex;
                    if (currentColumnIndex < gridColumns.length) {
                        const field = gridColumns[currentColumnIndex].field;
                        newRow[field] = cellValue;
                    }
                });
                newRowsToAdd.push(newRow);
            } else {
                let targetRow = updatedRows[currentRowIndex];
                pastedRow.forEach((cellValue, cellIndex) => {
                    const currentColumnIndex = startColumnIndex + cellIndex;
                    if (currentColumnIndex < gridColumns.length) {
                        const field = gridColumns[currentColumnIndex].field;
                        targetRow[field] = cellValue;
                    }
                });
            }
        });
        setGridRows([...updatedRows, ...newRowsToAdd]);
    } catch (error) {
        console.error("Paste Error:", error);
        alert("Ocurrió un error al pegar los datos. Verifique el formato.");
    }
  };

  // Funciones de cálculo automático
  const calculateMonto = (cantidad, precio) => {
    if (!cantidad || !precio) return 0;
    return parseFloat(cantidad) * parseFloat(precio);
  };

  const calculateGanancia = (operacion, precio, precioCP, cantidad) => {
    if (!operacion || !precio || !precioCP || !cantidad) return 0;
    const diferenciaPrecio = operacion === 'Compra' 
      ? parseFloat(precioCP) - parseFloat(precio)
      : parseFloat(precio) - parseFloat(precioCP);
    return diferenciaPrecio * parseFloat(cantidad);
  };

  const processRowUpdate = (newRow) => {
    // Calcular campos automáticamente
    const updatedRow = { ...newRow };
    
    // Calcular monto total
    if (updatedRow.cantidad && updatedRow.precio) {
      updatedRow.monto = calculateMonto(updatedRow.cantidad, updatedRow.precio);
    }
    
    // Calcular ganancia/fee
    if (updatedRow.operacion && updatedRow.precio && updatedRow.precioCarteraPropia && updatedRow.cantidad) {
      updatedRow.ganancia = calculateGanancia(updatedRow.operacion, updatedRow.precio, updatedRow.precioCarteraPropia, updatedRow.cantidad);
    }

    setGridRows(gridRows.map(row => row.id === updatedRow.id ? updatedRow : row));
    return updatedRow;
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    const rowsToSave = [];
    const rowsToKeep = [];
    let allSubmittedRowsAreValid = true;

    for (const row of gridRows) {
        const isEmpty = !row.fecha && !row.especie && !row.cliente;
        if (isEmpty) {
            rowsToKeep.push(row);
            continue;
        }
        try {
            await operationSchema.validate(row, { abortEarly: false });
            const { id, ...rowData } = row;
            rowsToSave.push({...rowData, operador: userName});
        } catch (err) {
            console.error(`Validation errors for row ${row.id}:`, err.errors);
            allSubmittedRowsAreValid = false;
            rowsToKeep.push(row);
        }
    }

    if (!allSubmittedRowsAreValid) {
        alert("Algunas filas tienen errores y no se guardarán. Corríjalas y vuelva a intentar.");
    }

    if (rowsToSave.length === 0) {
        alert("No hay operaciones válidas para guardar.");
        setIsSaving(false);
        return;
    }

    setGridRows(rowsToKeep.length > 0 ? rowsToKeep : [{ 
      id: 1, 
      fecha: '', 
      operacion: 'Compra', 
      especie: '', 
      plazo: '24Hs', 
      cliente: '', 
      cantidad: null, 
      precio: null, 
      moneda: 'Pesos',
      precioCarteraPropia: null, 
      mercado: 'Senebi',
      nroComitente: '', 
      arancelPPT: 0, 
      arancelROSVAL: 0, 
      ddm: 0, 
      fee: 0, 
      monto: 0,
      ganancia: 0,
      observaciones: '',
      cargada: 'No'
    }]);

    try {
        // Obtener token de autenticación
        const user = auth.currentUser;
        if (!user) {
            alert('Error: No hay usuario autenticado');
            return;
        }

        const idToken = await getIdToken(user);

        console.log('Payload enviado a bulkAddSenebiOperationsV2:', JSON.stringify({ operations: rowsToSave }, null, 2));

        // Usar fetch para llamar a la nueva función HTTP
        const response = await fetch('https://us-central1-planilla-operaciones-axon.cloudfunctions.net/bulkAddSenebiOperationsV2', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ operations: rowsToSave })
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (parseErr) {
                errorData = {};
            }
            console.error('Respuesta de error del servidor:', response.status, errorData);
            const serverMsg = errorData.error || `HTTP ${response.status}`;
            const details = errorData.details ? ` (${errorData.details})` : '';
            throw new Error(`${serverMsg}${details}`);
        }

        const result = await response.json();
        alert(result.message);
    } catch (error) {
        console.error("Error saving operations:", error);
        alert(`Error al guardar: ${error.message}. Las operaciones no guardadas han sido restauradas.`);
        setGridRows(prevRows => [...rowsToSave.map((r, i) => ({...r, id: (Math.max(...prevRows.map(pr => pr.id), 0) + i + 1)})), ...prevRows]);
    } finally {
        setIsSaving(false);
    }
  };

  const sortedOperations = useMemo(() => {
    const term = clienteFilter.trim().toLowerCase();
    const filtered = term
      ? operations.filter(op => op.cliente?.toLowerCase().includes(term))
      : operations;
    const sortableItems = [...filtered];
    if (sortConfig.key) {
      sortableItems.sort((a, b) =>
        compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction)
      );
    }
    return sortableItems;
  }, [operations, sortConfig, clienteFilter]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleToggleCargada = async (op) => {
      const opRef = doc(db, "senebiOperations", op.id);
      const newStatus = op.cargada === 'Si' ? 'No' : 'Si';
      await updateDoc(opRef, { cargada: newStatus });
  };

  const handleDeleteOperation = async (id) => {
      if (window.confirm("¿Está seguro que desea eliminar esta operación?")) {
          await deleteDoc(doc(db, "senebiOperations", id));
      }
  };

  // Función para eliminar una fila individual del grid (antes de guardar)
  const handleDeleteGridRow = (rowId) => {
    setGridRows(prevRows => {
      const filteredRows = prevRows.filter(row => row.id !== rowId);
      // Si no quedan filas, agregar una fila vacía por defecto
      if (filteredRows.length === 0) {
        return [{ 
          id: 1, 
          fecha: '', 
          operacion: 'Compra', 
          especie: '', 
          plazo: '24Hs', 
          cliente: '', 
          cantidad: null, 
          precio: null, 
          moneda: 'Pesos',
          precioCarteraPropia: null, 
          mercado: 'Senebi',
          nroComitente: '', 
          arancelPPT: 0, 
          arancelROSVAL: 0, 
          ddm: 0, 
          fee: 0, 
          monto: 0,
          ganancia: 0,
          observaciones: '',
          cargada: 'No'
        }];
      }
      return filteredRows;
    });
  };

  // Función para limpiar todas las filas del lote
  const handleClearAllRows = () => {
    if (window.confirm("¿Está seguro que desea eliminar todas las filas del lote? Esta acción no se puede deshacer.")) {
      setGridRows([{ 
        id: 1, 
        fecha: '', 
        operacion: 'Compra', 
        especie: '', 
        plazo: '24Hs', 
        cliente: '', 
        cantidad: null, 
        precio: null, 
        moneda: 'Pesos',
        precioCarteraPropia: null, 
        mercado: 'Senebi',
        nroComitente: '', 
        arancelPPT: 0, 
        arancelROSVAL: 0, 
        ddm: 0, 
        fee: 0, 
        monto: 0,
        ganancia: 0,
        observaciones: '',
        cargada: 'No'
      }]);
    }
  };

  // Funciones para manejar selección múltiple de operaciones
  const handleToggleSelectOperation = (operationId) => {
    setSelectedOperations(prev => {
      if (prev.includes(operationId)) {
        return prev.filter(id => id !== operationId);
      } else {
        return [...prev, operationId];
      }
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedOperations.length === sortedOperations.length) {
      // Si todas están seleccionadas, deseleccionar todas
      setSelectedOperations([]);
    } else {
      // Seleccionar todas las operaciones visibles
      setSelectedOperations(sortedOperations.map(op => op.id));
    }
  };

  const handleDeleteSelectedOperations = async () => {
    if (selectedOperations.length === 0) return;
    
    const confirmMessage = `¿Está seguro que desea eliminar ${selectedOperations.length} operación(es) seleccionada(s)? Esta acción no se puede deshacer.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      // Eliminar todas las operaciones seleccionadas
      const deletePromises = selectedOperations.map(id => 
        deleteDoc(doc(db, "senebiOperations", id))
      );
      
      await Promise.all(deletePromises);
      
      // Limpiar selección
      setSelectedOperations([]);
      
      alert(`Se eliminaron ${selectedOperations.length} operación(es) exitosamente.`);
    } catch (error) {
      console.error("Error eliminando operaciones:", error);
      alert(`Error al eliminar operaciones: ${error.message}`);
    }
  };

  // Función para parsear CSV correctamente respetando comillas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"' && (i === 0 || line[i-1] === ',')) {
        inQuotes = true;
      } else if (char === '"' && inQuotes && (i === line.length - 1 || line[i+1] === ',')) {
        inQuotes = false;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }
      
      if (!(char === '"' && (i === 0 || line[i-1] === ',' || i === line.length - 1 || line[i+1] === ','))) {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Sistema inteligente de mapeo de columnas CSV
  const createColumnMapper = (headerLine) => {
    const headers = parseCSVLine(headerLine).map(h => h.trim().replace(/^["']+|["']+$/g, ''));
    
    // Definir patrones de búsqueda para cada campo (flexibles y exactos)
    const fieldPatterns = {
      operacion: /^(operaci[oó]n|operation|tipo)$/i,
      especie: /^(especie|species|instrument|ticker)$/i,
      plazo: /^(plazo|settlement|liquidaci[oó]n)$/i,
      cliente: /^(cliente|client|customer)$/i,
      cantidad: /^(cantidad|quantity|nominal|vol)$/i,
      precio: /^(precio|price)$/i,
      monto: /^(monto|amount|total)$/i,
      moneda: /^(moneda|currency|ccy|divisa|coin|curr)$/i,
      precioCarteraPropia: /^(precio\s*cp|precio.*cp|cp)$/i,
      mercado: /^(mercado|market|exchange)$/i,
      operador: /^(operador|operator|trader|ejecutor)$/i,
      carga: /^(carga\s*|loaded|status)$/i,
      arancelPPT: /^(arancel\s*ppt|arancel.*ppt|ppt|.*arancel.*ppt.*)$/i,
      arancelROSVAL: /^(arancel\s*rosval|arancel.*rosval|rosval)$/i,
      ddm: /^(ddm|derechos)$/i,
      fee: /^(fee|comisi[oó]n|commission)$/i,
      ganancia: /^(ganancia|profit|pnl|resultado|fee\s*neto|fee.*neto)$/i,
      observaciones: /^(observaciones|comments|notes|obs)$/i
    };

    // Crear mapa de índices
    const columnMap = {};
    
    Object.keys(fieldPatterns).forEach(field => {
      const pattern = fieldPatterns[field];
      const index = headers.findIndex(header => pattern.test(header));
      if (index !== -1) {
        columnMap[field] = index;
        console.log(`Mapeado ${field} -> índice ${index} (${headers[index]})`);
      } else {
        console.warn(`No se encontró columna para ${field}`);
      }
    });
    
    return columnMap;
  };

  // Función para manejar ir a importación desde el recordatorio
  const handleGoToImport = () => {
    handleImportAction();
    // Activar el input de archivo para que sea más fácil seleccionar
    const fileInput = document.getElementById('csv-import-input');
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleCSVImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    const fileName = (file.name || '').toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    reader.onload = (e) => {
      try {
        let csv;
        if (isExcel) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // Convertir hoja a CSV con coma como separador para reusar el parser existente
          csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ',', blankrows: false });
        } else {
          csv = e.target.result;
        }
        // Eliminar BOM si está presente para que la regex de headers matchee la primera columna
        if (csv && csv.charCodeAt(0) === 0xFEFF) {
          csv = csv.slice(1);
        }
        const lines = csv.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert('El archivo CSV debe tener al menos una fila de encabezados y una fila de datos');
          setIsImporting(false);
          return;
        }

        console.log('Primeras líneas del CSV:', lines.slice(0, 5));

        // Detectar automáticamente la fila de encabezados
        let headerLineIndex = 0;
        let columnMap = null;
        
        // Buscar la línea que contenga los encabezados típicos
        for (let i = 0; i < Math.min(5, lines.length); i++) {
          const testMap = createColumnMapper(lines[i]);
          // Verificar que al menos tengamos los campos esenciales
          if (testMap.operacion !== undefined && testMap.especie !== undefined && testMap.cliente !== undefined) {
            headerLineIndex = i;
            columnMap = testMap;
            console.log(`Encabezados encontrados en línea ${i + 1}`);
            break;
          }
        }

        if (!columnMap) {
          alert('No se pudieron detectar automáticamente las columnas. Verifique que el archivo tenga encabezados como: Operación, Especie, Cliente, etc.');
          setIsImporting(false);
          return;
        }

        console.log('Mapeo de columnas detectado:', columnMap);
        console.log('Encabezados del CSV:', lines[headerLineIndex].split(','));

        // Detectar si hay desplazamiento entre encabezados y datos
        const sampleDataLine = lines[headerLineIndex + 1];
        const sampleCols = sampleDataLine ? sampleDataLine.split(',') : [];
        
        // Verificar si la primera columna de datos es numérica (indica índice de fila)
        if (sampleCols.length > 0) {
          const firstColValue = sampleCols[0]?.trim();
          if (firstColValue && /^\d+$/.test(firstColValue)) {
            // La primera columna de datos es un número (índice de fila)
            // pero los encabezados tienen esa columna vacía, no hay desplazamiento real
            console.log('Detectado índice de fila en columna 0, manteniendo mapeo original');
          }
        }

        // Filtrar filas de datos (saltar encabezados)
        const dataLines = lines.slice(headerLineIndex + 1).filter(line => {
          const cols = parseCSVLine(line);
          
          // Función auxiliar para limpiar y verificar valores
          const cleanValue = (value) => {
            if (!value) return '';
            return String(value).trim().replace(/^["']+|["']+$/g, '');
          };
          
          const operacion = cleanValue(cols[columnMap.operacion] || '');
          const especie = cleanValue(cols[columnMap.especie] || '');
          
          // Verificar que tenga datos válidos
          return operacion && 
                 (operacion === 'Compra' || operacion === 'Venta') &&
                 especie && especie !== '';
        });

        console.log('Filas de datos encontradas:', dataLines.length);

        const importedRows = dataLines.map((line, index) => {
          const cols = parseCSVLine(line);
          
          // Función auxiliar para obtener valor por mapeo
          const getFieldValue = (fieldName, isNumeric = false) => {
            const colIndex = columnMap[fieldName];
            if (colIndex === undefined) return isNumeric ? null : '';
            
            const value = cols[colIndex] || '';
            if (isNumeric) {
              const parsed = parseArgentineNumber(value, true);
              // Debug específico para campos problemáticos
              if (['arancelPPT', 'arancelROSVAL', 'ddm', 'ganancia'].includes(fieldName) && value !== '') {
                console.log(`DEBUG ${fieldName} - Raw: "${value}", Parsed: ${parsed}`);
              }
              return parsed;
            } else {
              return String(value).trim().replace(/^["']+|["']+$/g, '');
            }
          };
          
          console.log(`Procesando fila ${index + 1}:`);
          console.log('- Datos básicos:', {
            operacion: getFieldValue('operacion'),
            especie: getFieldValue('especie'),
            cliente: getFieldValue('cliente'),
            moneda: getFieldValue('moneda')
          });
          // Obtener todos los valores numéricos una sola vez
          const cantidad = getFieldValue('cantidad', true);
          const precio = getFieldValue('precio', true);
          const monto = getFieldValue('monto', true);
          const precioCP = getFieldValue('precioCarteraPropia', true);
          const ganancia = getFieldValue('ganancia', true);
          // Para aranceles, usar 0 como default en lugar de null
          const arancelPPT = getFieldValue('arancelPPT', true) || 0;
          const arancelROSVAL = getFieldValue('arancelROSVAL', true) || 0;
          const ddm = getFieldValue('ddm', true) || 0;
          // Fee se calculará automáticamente, no se lee del CSV

          console.log('- Datos numéricos:', {
            cantidad,
            precio,
            monto,
            precioCP
          });
          console.log('- Aranceles y valores (con índices):', {
            arancelPPT: `idx${columnMap.arancelPPT}: "${cols[columnMap.arancelPPT] || 'undefined'}"`,
            arancelROSVAL: `idx${columnMap.arancelROSVAL}: "${cols[columnMap.arancelROSVAL] || 'undefined'}"`,
            ddm: `idx${columnMap.ddm}: "${cols[columnMap.ddm] || 'undefined'}"`,
            ganancia: `idx${columnMap.ganancia}: "${cols[columnMap.ganancia] || 'undefined'} (datos reales del blotter)"`
          });
          console.log('- Valores de ejemplo por índice:', {
            'idx10': cols[10], // Mercado esperado
            'idx11': cols[11], // Operador esperado  
            'idx12': cols[12], // Carga esperado
            'idx13': cols[13], // ArancelPPT esperado
            'idx14': cols[14], // ArancelROSVAL esperado
            'idx15': cols[15], // DDM esperado
            'idx16': cols[16]  // FEE esperado
          });
          console.log('- Otros:', {
            operador: `"${cols[columnMap.operador] || 'undefined'}"`,
            mercado: getFieldValue('mercado'),
            observaciones: `"${cols[columnMap.observaciones] || 'undefined'}"`
          });
          
          // Funciones de mapeo para valores específicos
          const mapMoneda = (value) => {
            if (!value || value === '' || value === null || value === undefined) {
              console.log('mapMoneda: valor vacío/nulo, usando default Pesos');
              return 'Pesos';
            }
            const v = String(value).toLowerCase().trim();
            console.log(`mapMoneda: procesando "${value}" -> "${v}"`);
            
            // USD MEP variations
            if (v.includes('mep') || (v.includes('usd') && v.includes('mep'))) {
              console.log('mapMoneda: detectado USD Mep');
              return 'USD Mep';
            }
            // USD CCL variations  
            if (v.includes('ccl') || v.includes('cable') || (v.includes('usd') && v.includes('ccl'))) {
              console.log('mapMoneda: detectado USD CCL');
              return 'USD CCL';
            }
            // Pesos variations
            if (v.includes('peso') || v.includes('ars') || v === '$' || v === 'pesos') {
              console.log('mapMoneda: detectado Pesos');
              return 'Pesos';
            }
            // Generic USD (default to MEP)
            if (v === 'usd' || v === 'us$' || v === 'dolar' || v === 'dólar') {
              console.log('mapMoneda: USD genérico, mapeando a USD Mep');
              return 'USD Mep';
            }
            
            console.log(`mapMoneda: valor no reconocido "${v}", usando default Pesos`);
            return 'Pesos'; // Default
          };
          
          // Debug específico para moneda después de definir la función
          console.log('- DEBUG MONEDA ESPECÍFICO:', {
            monedaRaw: `"${cols[columnMap.moneda] || 'COLUMN_NOT_FOUND'}"`,
            monedaIndex: columnMap.moneda,
            monedaProcessed: getFieldValue('moneda'),
            monedaMapped: mapMoneda(getFieldValue('moneda'))
          });
          
          const mapPlazo = (value) => {
            if (!value) return '24Hs';
            const v = value.toLowerCase().trim();
            if (v === '24' || v === '24hs' || v === '24 hs') return '24Hs';
            if (v === '48' || v === '48hs' || v === '48 hs') return '48Hs';
            if (v === 'ci' || v === 'contado inmediato') return 'CI';
            if (v === 't+0' || v === 't0') return 'T+0';
            if (v === 't+1' || v === 't1') return 'T+1';
            if (v === 't+2' || v === 't2') return 'T+2';
            if (v === 't+3' || v === 't3') return 'T+3';
            return '24Hs'; // Default
          };

          // Mapear campos usando el sistema flexible
          const row = {
            id: Math.random() * 1000000, // ID temporal único
            fecha: new Date().toISOString().split('T')[0], // Fecha actual por defecto
            operacion: getFieldValue('operacion') || 'Compra',
            especie: getFieldValue('especie') || '',
            plazo: mapPlazo(getFieldValue('plazo')),
            cliente: getFieldValue('cliente') || '',
            cantidad: cantidad,
            precio: precio,
            monto: monto,
            moneda: mapMoneda(getFieldValue('moneda')),
            precioCarteraPropia: precioCP,
            mercado: getFieldValue('mercado') || 'Senebi',
            nroComitente: getFieldValue('operador') || '',
            arancelPPT: arancelPPT,
            arancelROSVAL: arancelROSVAL,
            ddm: ddm,
            fee: 0, // Fee será calculado automáticamente
            ganancia: ganancia || 0, // Ganancia viene del CSV (datos reales del blotter)
            observaciones: getFieldValue('observaciones') || '',
            cargada: getFieldValue('carga') === 'Si' ? 'Si' : 'No'
          };

          console.log(`DEBUG - Row después de asignación inicial:`, {
            monto: row.monto,
            ganancia: row.ganancia,
            arancelPPT: row.arancelPPT
          });

          // Calcular monto si no viene en el CSV o está vacío
          // NOTA: En el mercado financiero argentino, muchos instrumentos se cotizan "por cada 100"
          if (cantidad && precio && (monto === null || monto === 0)) {
            row.monto = (cantidad * precio) / 100;
            console.log(`Calculando monto: (${cantidad} * ${precio}) / 100 = ${row.monto}`);
          }
          
          // Calcular FEE automáticamente (lo que antes era ganancia calculada)
          if (row.operacion && precio && precioCP && cantidad) {
            row.fee = row.operacion === 'Compra' 
              ? ((precioCP - precio) * cantidad) / 100
              : ((precio - precioCP) * cantidad) / 100;
            console.log(`Calculando fee: ${row.operacion} - ((${row.operacion === 'Compra' ? precioCP + ' - ' + precio : precio + ' - ' + precioCP}) * ${cantidad}) / 100 = ${row.fee}`);
          }

          console.log(`DEBUG - Row FINAL antes de return:`, {
            monto: row.monto,
            ganancia: row.ganancia,
            arancelPPT: row.arancelPPT
          });

          return row;
        });

        console.log('Filas procesadas:', importedRows);
        console.log('DEBUG - Ganancia y Monto en primera fila:', {
          ganancia: importedRows[0]?.ganancia,
          monto: importedRows[0]?.monto,
          arancelPPT: importedRows[0]?.arancelPPT
        });
        console.log('DEBUG - TODAS las propiedades del primer row:', Object.keys(importedRows[0] || {}));
        console.log('DEBUG - Primer row completo:', importedRows[0]);

        if (importedRows.length === 0) {
          alert('No se encontraron operaciones válidas en el archivo CSV. Verifique que el archivo tenga datos de operaciones (Compra/Venta) válidos.');
          setIsImporting(false);
          return;
        }

        // Agregar las filas importadas al grid
        const maxId = gridRows.length > 0 ? Math.max(...gridRows.map(r => r.id)) : 0;
        const newRows = importedRows.map((row, index) => ({
          ...row,
          id: maxId + index + 1
        }));

        console.log('DEBUG - newRows con id añadido:', {
          ganancia: newRows[0]?.ganancia,
          monto: newRows[0]?.monto,
          arancelPPT: newRows[0]?.arancelPPT,
          id: newRows[0]?.id
        });
        console.log('DEBUG - newRows[0] completo:', newRows[0]);
        console.log('DEBUG - Propiedades de newRows[0]:', Object.keys(newRows[0] || {}));

        setGridRows(prev => [...prev.filter(row => {
          // Mantener solo las filas que no estén vacías
          return row.fecha || row.especie || row.cliente;
        }), ...newRows]);

        alert(`Se importaron ${importedRows.length} operaciones del archivo CSV`);
        
      } catch (error) {
        console.error('Error procesando archivo:', error);
        alert(`Error al procesar el archivo: ${error.message}. Verifique el formato del archivo.`);
      } finally {
        setIsImporting(false);
        event.target.value = ''; // Limpiar el input
      }
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file, 'UTF-8');
    }
  };

  return (
    <TabContainer>
      <TabHeader>
        <Title>Operaciones SENEBI</Title>
      </TabHeader>
      {canModify && (
          <>
            {/* Fuera de la sección plegable: el recordatorio de importación lo
                abre aunque en el celular la carga en lote esté cerrada. */}
            <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleCSVImport}
                disabled={isImporting}
                style={{ display: 'none' }}
                id="csv-import-input"
            />
            <MobileCollapsibleSection title="Carga en lote" summary="Planilla editable y blotter">
            <SubTitle>Agregar Nuevas Operaciones en Lote</SubTitle>
            <GridContainer onPaste={handlePaste}>
                <DataGrid
                    rows={gridRows}
                    columns={gridColumns}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    processRowUpdate={processRowUpdate}
                    editMode="cell"
                    onCellKeyDown={handleCellKeyDown}
                    onCellClick={handleCellClick}
                    isCellEditable={() => !isSaving}
                />
            </GridContainer>
            <ButtonsContainer className="axon-wrap">
                <Button variant="contained" onClick={handleAddNewRow} disabled={isSaving}>
                    Agregar Fila
                </Button>
                <Button variant="contained" color="primary" onClick={handleSaveAll} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : 'Guardar Operaciones'}
                </Button>
                <Button variant="outlined" color="error" onClick={handleClearAllRows} disabled={isSaving}>
                    Limpiar Todo el Lote
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <label htmlFor="csv-import-input">
                        <Button variant="outlined" component="span" disabled={isImporting}>
                            {isImporting ? <CircularProgress size={24} /> : 'Importar Blotter'}
                        </Button>
                    </label>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Formato: Blotter SENEBI (.csv, .xlsx, .xls)
                    </Typography>
                </Box>
            </ButtonsContainer>
            </MobileCollapsibleSection>
          </>
      )}
      <IndicatorsContainer>
        <Indicator><strong>{formatCurrency(feeTotals.Pesos, 'Pesos')}</strong><span>Fee Total Pesos</span></Indicator>
        <Indicator><strong>{formatCurrency(feeTotals['USD Mep'], 'USD Mep')}</strong><span>Fee Total USD Mep</span></Indicator>
        <Indicator><strong>{formatCurrency(feeTotals['USD CCL'], 'USD CCL')}</strong><span>Fee Total USD CCL</span></Indicator>
      </IndicatorsContainer>

      {canModify && selectedOperations.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteSelectedOperations}
          >
            Eliminar Seleccionadas ({selectedOperations.length})
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setSelectedOperations([])}
          >
            Deseleccionar Todas
          </Button>
        </Box>
      )}

      <SearchInput
        className="axon-fill"
        type="text"
        placeholder="Buscar cliente..."
        value={clienteFilter}
        onChange={e => setClienteFilter(e.target.value)}
      />

      <TableContainer>
        <Table ref={stackedTable.ref} className={stackedTable.className}>
            <thead>
                <tr>
                    {canModify && (
                      <th style={{ width: '50px' }}>
                        <input 
                          type="checkbox" 
                          checked={sortedOperations.length > 0 && selectedOperations.length === sortedOperations.length}
                          onChange={handleToggleSelectAll}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                    )}
                    <th onClick={() => requestSort('sequentialId')}>ID</th>
                    <th onClick={() => requestSort('fecha')}>Fecha</th>
                    <th onClick={() => requestSort('operacion')}>Operación</th>
                    <th onClick={() => requestSort('especie')}>Especie</th>
                    <th onClick={() => requestSort('plazo')}>Plazo</th>
                    <th onClick={() => requestSort('cliente')}>Cliente</th>
                    <th onClick={() => requestSort('cantidad')}>Cantidad</th>
                    <th onClick={() => requestSort('precio')}>Precio</th>
                    <th onClick={() => requestSort('precioCarteraPropia')}>Precio CP</th>
                    <th onClick={() => requestSort('monto')}>Monto</th>
                    <th onClick={() => requestSort('moneda')}>Moneda</th>
                    <th onClick={() => requestSort('ganancia')}>Ganancia</th>
                    <th onClick={() => requestSort('cargada')}>Cargada</th>
                    <th onClick={() => requestSort('observaciones')}>Observaciones</th>
                    <th>Acciones</th>
                </tr>
            </thead>
            <tbody>
                {sortedOperations.map((op) => (
                    <StyledTableRow key={op.id} $isLoaded={op.cargada === 'Si'}>
                        {canModify && (
                          <td>
                            <input 
                              type="checkbox" 
                              checked={selectedOperations.includes(op.id)}
                              onChange={() => handleToggleSelectOperation(op.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                        )}
                        <td>{op.sequentialId}</td>
                        <td>{op.fecha}</td>
                        <td>{op.operacion}</td>
                        <td>{op.especie}</td>
                        <td>{op.plazo || '24Hs'}</td>
                        <td>{op.cliente}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumberForDisplay(op.cantidad)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(op.precio, op.moneda || 'Pesos')}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(op.precioCarteraPropia, op.moneda || 'Pesos')}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(op.monto, op.moneda || 'Pesos')}</td>
                        <td>{op.moneda || 'Pesos'}</td>
                        <td style={{ textAlign: 'right', color: op.ganancia >= 0 ? '#10B981' : '#EF4444' }}>
                            {formatCurrency(op.ganancia, op.moneda || 'Pesos')}
                        </td>
                        <td>
                            <CargadaButton onClick={() => canModify && handleToggleCargada(op)} $isLoaded={op.cargada === 'Si'} disabled={!canModify}>
                                {op.cargada || 'No'}
                            </CargadaButton>
                        </td>
                        <td>{renderCommentCell(op.observaciones || op.comentario, 'Observaciones', onViewComment)}</td>
                        <td>
                            {canModify && userName === op.operador && (
                                <ActionContainer>
                                    <ActionButton color={theme.colors.danger} onClick={() => handleDeleteOperation(op.id)} title="Eliminar">
                                        <TrashIcon />
                                    </ActionButton>
                                </ActionContainer>
                            )}
                        </td>
                    </StyledTableRow>
                ))}
            </tbody>
        </Table>
      </TableContainer>

      {/* Modal de recordatorio de importación SENEBI */}
      <ImportSenebiReminderModal
        isOpen={shouldShowReminder}
        onGoToImport={handleGoToImport}
        onDismiss={dismissReminder}
        currentTime={currentTime}
      />
    </TabContainer>
  );
};

export default SenebiTab;
